import { Component, Host, h, Prop, ComponentInterface, Element } from '@stencil/core';
import { HTMLAnchorElementTarget } from '../../utils/helpers';

@Component({
  tag: 'vds-icon-link',
  styleUrl: 'vds-icon-link.scss',
  shadow: true,
})
export class VdsIconLink implements ComponentInterface {

  @Element() el: HTMLVdsIconLinkElement;

  private scaleClasses: string = '';
  private borderClasses: string = '';
  private tabletClasses: string = '';
  private displayClass: string = '';

  private iconSpan?: HTMLSpanElement;
  private labelSpan?: HTMLSpanElement;

  /**
   * Href
   */
  @Prop() href: string | undefined = undefined;

  /**
   * Target
   */
  @Prop() target: HTMLAnchorElementTarget = '_self';

  /**
   * Label
   */
  @Prop() label: string;

  /**
   * Last item
   */
  @Prop() last: boolean = false;

  /**
   * Icon color
   */
  @Prop() iconColor: 'berry' | 'grey' | 'grey-light' | 'grey-dark' = 'berry';

  /**
   * Sets icon width/height
   */
  @Prop() mobileIconSize: string = "28px";

  /**
   * Adds scale animation to icon
   */
  @Prop() animated: boolean = false;

  /**
   * Enables growth of items on tablet widths
   */
  @Prop() tabletGrowth: boolean = true;

  /**
   * Sets font-size to given value
   */
  @Prop() fontSize: string = "14px";

  /**
   * Sets line-height to given value
   */
  @Prop() lineHeight: string = "18px";

  /**
   * Label color
   */
  @Prop() labelColor: 'berry' | 'grey' | 'grey-light' | 'grey-dark' = 'berry';

  /**
   * Label font family
   */
  @Prop() fontFamily: 'regular' | 'bold' = 'regular';

  /**
   * Show underline
   */
  @Prop() showUnderline: boolean = true;

  /**
   * Show icon
   */
  @Prop() showIcon: boolean = true;

  /**
   * Switches color on user interaction
   */
  @Prop() interactionColor: '' | 'grey' = '';

  /**
   * Toggles download link functionality
   */
  @Prop() download: boolean = false;

  /**
   * Disables link
   */
  @Prop() disabled: boolean = false;

  componentDidRender(): void {
    this.setSpanStyles();
    this.addDownloadAttribute();
  }

  setSpanStyles(): void {
    this.labelSpan.style.fontSize = this.fontSize;
    this.labelSpan.style.lineHeight = this.lineHeight;
    this.labelSpan.style.fontFamily = this.setFontFamily(this.fontFamily);
    this.labelSpan.style.color = this.setColor(this.labelColor);
    this.iconSpan.style.color = this.setColor(this.iconColor);
    this.iconSpan.style.height = this.mobileIconSize;
    this.iconSpan.style.flexBasis = this.mobileIconSize;
  }

  setColor(inputColor: string): string {
    switch (inputColor) {
      case "berry":
        return 'var(--vt-color-berry-default, #D7004B)';
      case "grey":
        return 'var(--vt-color-grey-default, #4B4B46)';
      case "grey-light":
        return 'var(--vt-color-grey-light, #A5A5A2)';
      case "grey-dark":
        return 'var(--vt-color-grey-dark, #141412)';
      default:
        return '';
    }
  }

  setFontFamily(inputFontFamily: string): string {
    switch (inputFontFamily) {
      case "bold":
        return 'var(--vt-font-family-bold, "HelveticaNeueWorldW05-75Bd", "Helvetica Neue", "Helvetica", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")';
      default:
        return 'var(--vt-font-family-regular, "HelveticaNeueWorldW05-55Rm", "Helvetica Neue", "Helvetica", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji")';
    }
  }

  render(): void {
    const TagType = this.href ? 'a' : 'button' as any;
    const { href, label, target, last, animated, tabletGrowth, showUnderline, showIcon, mobileIconSize, labelColor, disabled } = this;

    let { borderClasses, scaleClasses, tabletClasses, displayClass } = this;

    if(!last) {
      borderClasses = "border-b-2 border-grey-lighter pb-[20px] mb-[20px] ";
    }

    if(animated) {
      scaleClasses = " transition-[transform] duration-[250ms] scale-100";
    }

    if(tabletGrowth) {
      tabletClasses = " sm:h-[46px] sm:basis-[46px]";
    }

    if(!showIcon) {
      displayClass = " hidden";
    }

    return (
      <Host>
        <div class={ borderClasses + "flex items-center" }>

          <TagType
            href={ href }
            target={ TagType === 'a' ? target : undefined }
            disabled={ TagType === 'button' && disabled }
            class={{
              'flex items-center w-full': true,
              'hover:underline': showUnderline,
              'decoration-berry': showUnderline && labelColor == "berry",
              'decoration-grey': showUnderline && labelColor == "grey",
              'decoration-grey-light': showUnderline && labelColor == "grey-light",
              'decoration-grey-dark': showUnderline && labelColor == "grey-dark",
              'cursor-not-allowed': TagType === 'a' && disabled,
              'opacity-25': disabled
            }}
            ref={ nativeEl => this.addReferenceToAnimatedIcon(nativeEl) }
            onMouseOver={() => this.setInteractionColor()}
            onMouseOut={() => this.removeInteractionColor()}
            onFocus={() => this.setInteractionColor()}
            onBlur={() => this.removeInteractionColor()}>
            <div class="flex items-center w-full">
              <span
                class={
                  scaleClasses + tabletClasses + displayClass + " mr-[10px] mt-[5px] flex-none leading-none overflow-hidden basis-[" + mobileIconSize + "] md:basis-[28px] h-[" + mobileIconSize + "] md:h-[28px]"
                }
                ref={el => this.iconSpan = el as HTMLSpanElement}>

                <slot name="icon-start"></slot>

              </span>
              <span
                class="w-full break-words text-left"
                ref={el => this.labelSpan = el as HTMLSpanElement}
                innerHTML={ label }>
                <slot name="label-icon"></slot>
              </span>
            </div>
          </TagType>

          <slot name="icon-end"></slot>

        </div>
      </Host>
    );
  }

  private addReferenceToAnimatedIcon(refEl: HTMLElement): void {
    const child = this.el.querySelector('vds-animated-icon');
    if (child) {
      child.interactionElement = refEl;
    }
  }

  private setInteractionColor(): void {
    if (!this.disabled) {
      this.labelSpan.style.color = this.setColor(this.interactionColor);
    }
  }

  private removeInteractionColor(): void {
    if (!this.disabled) {
      this.labelSpan.style.color = this.setColor(this.labelColor);
    }
  }

  private addDownloadAttribute(): void {
    if(this.download) {
      this.el.shadowRoot.querySelector('a').setAttribute('download', '');
    }
  }

}
